import { Dropdown, Flex, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomIcon from "../../layouts/CustomIcon";
import { PRODUCTS } from "../../../actions/constants";
import { FROM_PARAM, FROM_PROJECT_VALUE } from "../../../utills/paths";
import { useAuthContext } from "../../user/AuthProvider";

const { Text } = Typography;

/** The coloured disc behind each study type's icon, matching `.create-study-menu__icon`. */
const ICON_DISC_SIZE = 28;

/**
 * The artwork drawn on that disc.
 *
 * Every product icon but one is a badge that carries its own circle in the product's colour —
 * the same colour the disc behind it is painted — so the artwork can be drawn right up to the
 * disc's edge without a ring of a second circle appearing. At 16px the white glyph inside that
 * circle came out around 10px and read as a speck; 24 gives it half again as much room while
 * the 2px of disc still showing is the same colour as the badge, so the disc looks untouched.
 */
const ICON_GLYPH_SIZE = 24;

/** Breathing room between the bottom of the menu and the bottom of the window. */
const VIEWPORT_GAP = 16;

/**
 * Below this the menu is a sliver, and scrolling it is worse than letting antd shift it upwards —
 * which is what it does on its own once the menu is short enough to move.
 */
const MIN_MENU_HEIGHT = 220;

/**
 * The study type picker behind Create New Study.
 *
 * Every entry hands off to that type's existing creation screen rather than reimplementing it,
 * carrying the project so the study is filed there instead of the workspace default. The three
 * moderated entries are the exception: their creation is a modal inside the moderated dashboard,
 * so they open it there — see `create` in the query string.
 *
 * Types the workspace has no access to are dropped, since `products` already reflects that. A
 * type can also name a feature flag it needs, for the ones gated separately from the product.
 */

/**
 * The destination for one study type, given the project it should be filed in — or none — and the
 * page the user is starting from.
 *
 * `projectId` is optional because this menu also sits on the workspace-wide All Studies page and
 * in the sidebar, where there is no project to file into and the server picks the workspace
 * default. An empty `project_id` would be read as a project id of its own, so it is dropped
 * rather than sent blank.
 *
 * `origin` is separate from it on purpose. It is not about where the study belongs but about
 * where the user was, which is where Cancel has to return them — and the two differ at both ends:
 * All Studies has an origin and no project, while a dashboard creating into one of its folders
 * passes a project and must still return to itself.
 */
const buildLink = (path, params, projectId, origin = null) => {
  const query = new URLSearchParams(params);

  if (projectId) query.set("project_id", String(projectId));
  if (origin) query.set(FROM_PARAM, origin);

  const search = query.toString();

  return search ? `${path}?${search}` : path;
};

const GROUPS = [
  {
    title: "Usability Testing",
    items: [
      {
        key: PRODUCTS.UNMODERATED,
        label: "Unmoderated (Advanced)",
        hint: "Screen, cam & audio recording",
        icon: "unmoderated",
        to: (projectId, origin) => buildLink("/test/new", {}, projectId, origin),
      },
      {
        key: PRODUCTS.NORECORDING,
        label: "Unmoderated (In-Browser)",
        hint: "Cam & audio only – no screen recording",
        icon: "nav_product_icon",
        /**
         * Drawn to fill the disc, unlike the rest.
         *
         * This artwork is a 48px badge — its own navy circle with a 1.5px white glyph on it —
         * where the others are 32px. Scaled to the glyph size the rest use, the stroke lands on
         * half a pixel and goes soft, and the badge's own #2146A5 circle sits inside the
         * wrapper's #2753C6 one, leaving a ring of a not-quite-matching blue — the one icon here
         * whose circle is not the product's colour, so the one that cannot be drawn short of the
         * disc's edge. Drawing it at the wrapper's own size fixes both: the stroke gets the most
         * room of any of them, and the badge covers the wrapper exactly, which is how the same
         * icon is drawn on the home page.
         */
        iconSize: ICON_DISC_SIZE,
        to: (projectId, origin) => buildLink("/test/new", { recording: "false", survey: "false" }, projectId, origin),
      },
    ],
  },
  {
    title: "Interviews & Group Discussions",
    items: [
      {
        key: PRODUCTS.MODERATED,
        label: "User Interviews (1:1 / Dyad / Triad)",
        hint: "Screen, cam & audio recording",
        icon: "moderated",
        to: (projectId) => buildLink("/moderated", { create: "interview" }, projectId),
      },
      {
        key: PRODUCTS.MODERATED,
        id: "ai",
        label: "AI Moderated User Interview",
        hint: "Screen, cam & audio recording",
        icon: "ai_moderated_filled",
        /**
         * Drawn smaller than the rest, for the opposite reason to the Figma one above.
         *
         * This artwork is a solid head that all but fills its 24px box, where the others are
         * badges whose own circle is the disc — so at the shared glyph size it ran into the
         * disc's edge instead of sitting on it. 20 gives it the ring of green the neighbouring
         * interview icons have inside their own circles.
         */
        iconSize: 20,
        badge: "NEW",
        // Comes with the moderated product but is enabled per organisation, so the product
        // check alone offered it to workspaces that cannot use it. Same flag the moderated
        // dashboard gates its own AI entry points on.
        feature: "ai_interview",
        /**
         * Carries the project, like the other two moderated types.
         *
         * An AI interview is configured against a moderated study rather than created directly
         * into a project, so this opens the dialog where that study is chosen — but that dialog
         * can also make a new study, and one made from inside a project belongs to it. Without
         * this the new study was filed in the workspace default instead.
         */
        to: (projectId) => buildLink("/moderated", { create: "ai" }, projectId),
      },
      {
        key: PRODUCTS.MODERATED,
        id: "focus-group",
        label: "Focus Group Discussion",
        hint: "Screen, cam & audio recording",
        icon: "moderated",
        to: (projectId) => buildLink("/moderated", { create: "focus-group" }, projectId),
      },
    ],
  },
  {
    title: "Information Architecture",
    items: [
      {
        key: PRODUCTS.CARDSORTING,
        label: "Card Sorting",
        hint: "No session recording",
        icon: "card_sorting",
        to: (projectId) => buildLink("/tests/new/card-sorting", {}, projectId),
      },
      {
        key: PRODUCTS.TREETESTING,
        label: "Tree Testing",
        hint: "No session recording",
        icon: "tree_testing",
        to: (projectId) => buildLink("/tests/new/tree-testing", {}, projectId),
      },
    ],
  },
  {
    title: "Quantitative",
    items: [
      {
        key: PRODUCTS.SURVEYS,
        label: "Surveys",
        hint: "No session recording",
        icon: "surveys",
        to: (projectId, origin) => buildLink("/test/new", { recording: "false", survey: "true" }, projectId, origin),
      },
    ],
  },
];

/**
 * `children` is the trigger — a Create New Study button on the project and All Studies pages, the
 * sidebar's own nav row in the app shell.
 *
 * The last three props exist for that sidebar. It opens its menus to the right rather than below;
 * on a narrow screen it is a drawer that has to close itself once the user has gone somewhere; and
 * its trigger sits further down the window than a page's does, which is what `variant` is for.
 *
 * `variant` names where the trigger sits, and that settles how tall the menu may be. The three
 * places differ by how much of the window is above the menu, and in how knowable that is.
 *
 * The two sidebar triggers sit in a column that starts at the top of the window, so the room
 * beneath them is the viewport minus a roughly fixed offset, and the stylesheet can say so. A
 * page's button cannot be written down that way: it sits under whatever the page put above it —
 * a project's name, its description, its labels, a toolbar — so on the project page it can be
 * four hundred pixels down, and the same CSS that fits one page overflows another. That one is
 * measured when it opens.
 */
export default function CreateStudyMenu({
  projectId = null,
  /**
   * The page this menu is on, carried into the creation screen so its Cancel can come back here.
   *
   * A project page says so by passing its id; the All Studies page has no id to pass, so it says
   * it here instead. The sidebar passes neither: it is on every page rather than being one, so
   * there is nowhere in particular to return to and the creation screen keeps its own default.
   */
  origin = projectId ? FROM_PROJECT_VALUE : null,
  placement = "bottomRight",
  variant = "page",
  onNavigate = () => {},
  children,
}) {
  const navigate = useNavigate();
  const { products, allowedFeatures } = useAuthContext();

  const [open, setOpen] = useState(false);

  /**
   * How much room is actually under the trigger, published as a custom property for the
   * stylesheet to cap the menu with.
   *
   * Two things make this the shape it is. The measurement cannot be made before the menu exists,
   * because where antd puts it is the thing being measured — so it happens a frame after it
   * opens, and the CSS value is the fallback until then. And the number goes to the document
   * rather than to the menu's own `style`, because antd 6 rebuilds the menu's `style` and
   * `classNames` as it renders: an earlier attempt to hand the height over that way never
   * arrived at all.
   *
   * Without the cap the menu is laid out at its full height inside a portal that is positioned
   * absolutely in the body, so an oversized one extends the document — which is why the page
   * itself gained a scrollbar, and why the last group was both below the fold and unreachable.
   */
  useEffect(() => {
    // The sidebar's offsets are fixed and already expressed in the stylesheet; only a page's
    // trigger moves with the content above it.
    if (!open || variant !== "page") return undefined;

    const root = document.documentElement;

    const measure = () => {
      // A closed dropdown stays in the DOM, hidden, so the open one has to be asked for by name —
      // otherwise a leftover from the last time it was open answers first.
      const menu = document.querySelector(".create-study-menu--page:not(.ant-dropdown-hidden) .ant-dropdown-menu");

      if (!menu) return;

      const room = window.innerHeight - menu.getBoundingClientRect().top - VIEWPORT_GAP;

      root.style.setProperty("--create-study-menu-max", `${Math.max(MIN_MENU_HEIGHT, Math.round(room))}px`);
    };

    const frame = window.requestAnimationFrame(measure);

    /**
     * Measured a second time once it has settled.
     *
     * Capping the menu changes its height, and antd re-aligns a popup whose size changes — a menu
     * it had shifted upwards to make the full thing fit can drop back down beside its trigger now
     * that it is shorter, leaving the first measurement describing a position it no longer holds.
     * The second reading is taken from wherever it ended up. It converges rather than oscillates:
     * once the menu fits in the room below the trigger, that is where antd leaves it.
     */
    const settle = window.setTimeout(measure, 250);

    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      window.removeEventListener("resize", measure);
      // Cleared rather than left behind: the next open is somewhere else on the page, and a stale
      // height would be applied to it for a frame before the new one is measured.
      root.style.removeProperty("--create-study-menu-max");
    };
  }, [open, variant]);

  const available = new Set((products || []).map((product) => product.key));

  /** A type is offered when its product is available and any feature it names is enabled. */
  const isOffered = (item) => available.has(item.key) && (!item.feature || Boolean(allowedFeatures?.[item.feature]?.enabled));
  const colorFor = (key) => (products || []).find((product) => product.key === key)?.color || "#6C6C89";

  const routes = new Map();
  const items = [];

  GROUPS.forEach((group) => {
    const allowed = group.items.filter(isOffered);

    if (!allowed.length) return;

    items.push({ key: `group:${group.title}`, type: "group", label: group.title });

    allowed.forEach((item) => {
      const key = item.id ? `${item.key}:${item.id}` : item.key;

      routes.set(key, item.to);

      items.push({
        key,
        label: (
          <Flex align='center' gap={10}>
            <span className='create-study-menu__icon' style={{ background: colorFor(item.key) }}>
              <CustomIcon name={item.icon} width={item.iconSize || ICON_GLYPH_SIZE} height={item.iconSize || ICON_GLYPH_SIZE} />
            </span>
            <div style={{ minWidth: 0 }}>
              <Flex align='center' gap={8}>
                <Text className='fw-500 color-121217'>{item.label}</Text>
                {item.badge && (
                  <Tag color='green' className='create-study-menu__badge'>
                    {item.badge}
                  </Tag>
                )}
              </Flex>
              <Text className='font-size-12px color-6C6C89'>{item.hint}</Text>
            </div>
          </Flex>
        ),
      });
    });
  });

  return (
    <Dropdown
      trigger={["click"]}
      placement={placement}
      // Watched, not controlled: antd still opens and closes itself, and this only says when,
      // so the menu can be measured while it is on screen.
      onOpenChange={setOpen}
      // `overlayClassName` is deprecated in antd 6 in favour of `classNames.root`; it still
      // works but warns on every render.
      /**
       * How tall the menu may be is settled in CSS, by this class.
       *
       * It was measured in JS — the room beneath the trigger, read after the popup was placed —
       * which was both fragile and, as it turned out, inert: the result was handed to the menu
       * through `menu.style`, and antd 6's Dropdown rebuilds the menu's own `style` and
       * `classNames` as it renders it, so the height never arrived. Two numbers in a stylesheet do
       * the same job without depending on when antd calls what.
       */
      classNames={{ root: `create-study-menu create-study-menu--${variant}` }}
      menu={{
        items,
        onClick: ({ key }) => {
          const to = routes.get(key);

          if (!to) return;

          /**
           * No toast on the way out.
           *
           * The three moderated types hand off to another page, and this used to raise an
           * "Opening study setup…" message to cover that wait. The destination has a loading
           * state of its own, so the message was a second spinner saying the same thing — and on
           * the AI route it outlived the handoff and sat there alone.
           */
          navigate(to(projectId, origin));
          onNavigate();
        },
      }}
    >
      {children}
    </Dropdown>
  );
}
